import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEdit, faTrash, faPlay, faExclamationTriangle, faFileText, faRefresh, faInfoCircle, faRocket, faEyeSlash, faRightLeft } from '@/fontawesome';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import VideoUploadButton from '@/components/admin/academy/VideoUploadButton.tsx';
import VideoProcessingStatus from '@/components/videos/VideoProcessingStatus';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import PublishModal from '@/components/PublishModal';
import AutoPublishModal, { type AutoPublishSettings } from '@/components/AutoPublishModal';
import { getVideoProcessingStatus, isVideoActivelyProcessing, isVideoReadyToPlay } from '@/utils/videoProcessing';
import type { Video, VideoID } from '@/models';
import { AdminVideoAttachments } from '@/components/admin/academy/AdminVideoAttachments';
import { showSuccessToast, showErrorToast, handleAPIError } from '@/utils/toasts';

interface AdminVideoCardProps {
  video: Video;
  onVideoUploaded: (video: Video) => void;
  onVideoUpdated: (video: Video) => void;
  onEditVideo: (video: Video) => void;
  onMoveVideo: (video: Video) => void;
  onDeleteVideo: (videoId: VideoID) => void;
  deletingVideo: VideoID | null;
  onShowKnowledge: (video: Video) => void;
  moduleNoKnowledge?: boolean;
}

const AdminVideoCard: React.FC<AdminVideoCardProps> = ({
  video: initialVideo,
  onVideoUploaded,
  onVideoUpdated,
  onEditVideo,
  onMoveVideo,
  onDeleteVideo,
  deletingVideo,
  onShowKnowledge,
  moduleNoKnowledge = false
}) => {
  const [video, setVideo] = useState<Video>(initialVideo);
  const [isResetting, setIsResetting] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isAutoPublishModalOpen, setIsAutoPublishModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingAutoPublish, setIsSavingAutoPublish] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Update local video state when prop changes (e.g., from parent updates)
  useEffect(() => {
    setVideo(initialVideo);
  }, [initialVideo]);

  // Auto-refresh for actively processing videos (paused during upload)
  useEffect(() => {
    if (!isVideoActivelyProcessing(video) || isUploading) {
      return; // No active processing or upload in progress, no need to refresh
    }

    // Set up interval to refresh every second for actively processing videos
    const refreshInterval = setInterval(async () => {
      try {
        const updatedVideo = await APIRoutes.getVideo(video.id);
        if (JSON.stringify(updatedVideo) === JSON.stringify(video)) return;
        setVideo(updatedVideo);

        // Also notify parent of the update
        onVideoUpdated(updatedVideo);

        console.log(`Auto-refreshed video ${video.id} due to active processing`);
      } catch (error) {
        console.error(`Error auto-refreshing video ${video.id}:`, error);
        // Don't show error toast for auto-refresh failures to avoid spamming user
      }
    }, 1000); // 1 second

    console.log(`Started 1-second auto-refresh for actively processing video: ${video.id}`);

    // Cleanup interval on unmount or when processing stops
    return () => {
      clearInterval(refreshInterval);
      console.log(`Cleared auto-refresh timer for video: ${video.id}`);
    };
  }, [video.localProcessingState, video.cdnUploadState, video.cdnProcessingState, video.id, onVideoUpdated, isUploading]);

  const processingStatus = getVideoProcessingStatus(video);

  const formatDuration = (duration?: number) => {
    if (!duration) return 'Unbekannt';
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor(duration % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleResetProcessing = async () => {
    const confirmed = window.confirm(
      'Videoverarbeitung wirklich neustarten?'
    );

    if (!confirmed) return;

    try {
      setIsResetting(true);
      const updatedVideo = await APIRoutes.resetVideoProcessing(video.id);
      setVideo(updatedVideo);
      onVideoUpdated(updatedVideo);
    } catch (error) {
      console.error('Error resetting video processing:', error);
      alert('Fehler beim Zurücksetzen. Bitte versuchen Sie es erneut.');
    } finally {
      setIsResetting(false);
    }
  };

  const handlePublish = async (sendDiscord: boolean, sendTelegram: boolean, sendMail: boolean, sendPush: boolean) => {
    try {
      setIsPublishing(true);
      const updatedVideo = await APIRoutes.publishVideo(video.id, sendDiscord, sendTelegram, sendMail, sendPush);
      setVideo(updatedVideo);
      onVideoUpdated(updatedVideo);
      showSuccessToast('Video erfolgreich veröffentlicht');
    } catch (error) {
      console.error('Error publishing video:', error);
      showErrorToast('Fehler beim Veröffentlichen des Videos');
      handleAPIError(error);
      throw error;
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm('Möchten Sie dieses Video wirklich zurückziehen?')) {
      return;
    }

    try {
      setIsPublishing(true);
      const updatedVideo = await APIRoutes.unpublishVideo(video.id);
      setVideo(updatedVideo);
      onVideoUpdated(updatedVideo);
      showSuccessToast('Video erfolgreich zurückgezogen');
    } catch (error) {
      console.error('Error unpublishing video:', error);
      showErrorToast('Fehler beim Zurückziehen des Videos');
      handleAPIError(error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleAutoPublishUpdate = async (settings: AutoPublishSettings) => {
    try {
      setIsSavingAutoPublish(true);
      const updatedVideo = await APIRoutes.updateVideo(video.id, {
        title: video.title,
        description: video.description,
        order: video.order,
        ...settings
      });
      setVideo(updatedVideo);
      onVideoUpdated(updatedVideo);
      showSuccessToast('Auto-Publish Einstellungen gespeichert');
    } catch (error) {
      console.error('Error updating auto-publish settings:', error);
      showErrorToast('Fehler beim Speichern der Einstellungen');
      handleAPIError(error);
      throw error;
    } finally {
      setIsSavingAutoPublish(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
      {/* Local Processing Info Alert */}
      {video.localProcessingState === 'ACTIVE' && video.localProcessingMessage && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="w-5 h-5 text-blue-500 mr-2" />
            <div>
              <h5 className="font-medium text-blue-800 dark:text-blue-200">Lokale Verarbeitung</h5>
              <p className="text-sm text-blue-600 dark:text-blue-300 mt-1 whitespace-pre-wrap">
                {video.localProcessingMessage.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {processingStatus.showErrorAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-500 mr-2" />
            <div>
              <h5 className="font-medium text-red-800 dark:text-red-200">Verarbeitungsfehler</h5>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1 whitespace-pre-wrap">
                {processingStatus.errorMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-4">
        <div className="md:col-span-1">
          <div className={`aspect-video relative overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700${video.hiddenByModuleLimit ? ' opacity-20' : ''}`}>
            {video.thumbnail ? (
              <img
                src={APIRoutes.getBlobContentUrl(video.thumbnail)}
                alt={video.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <FontAwesomeIcon icon={faPlay} className="w-8 h-8 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h4 className={`font-semibold text-gray-900 dark:text-white mb-1${video.hiddenByModuleLimit ? ' line-through' : ''}`}>
                {video.title}
              </h4>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  video.published && processingStatus.canSetPublished
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {video.published && processingStatus.canSetPublished ? 'Veröffentlicht' : 'Entwurf'}
                </span>
                {video.hiddenByModuleLimit && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200">
                    <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3 mr-1" />
                    Ausgeblendet
                  </span>
                )}
                {video.providesKnowledge && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    Knowledge
                  </span>
                )}
                {!processingStatus.canSetPublished && video.published && (
                  <span className="text-xs text-yellow-600 dark:text-yellow-400">
                    (Veröffentlichung gesperrt)
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mb-3">
            <MarkdownRenderer className="text-sm">
              {video.description}
            </MarkdownRenderer>
          </div>

          {/* Upload Button */}
          {processingStatus.showUploadButton && (
            <div className="mb-3">
              <VideoUploadButton
                videoId={video.id}
                moduleNoKnowledge={moduleNoKnowledge}
                onVideoUpdated={(updatedVideo) => {
                  if (!updatedVideo) {
                    console.error('AdminVideoCard: onVideoUpdated called with undefined video');
                    return;
                  }

                  console.log('AdminVideoCard: Video updated:', updatedVideo);
                  setVideo(updatedVideo);
                  onVideoUploaded(updatedVideo);
                }}
                onUploadingChange={setIsUploading}
                className="text-sm px-3 py-1.5"
              />
            </div>
          )}

          {/* Processing States */}
          {processingStatus.showProcessingStates && (
            <div className="mb-3">
              <VideoProcessingStatus video={video} compact={true} />
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
            <div>Dauer: {formatDuration(video.duration ?? undefined)}</div>
            <div>Reihenfolge: {video.order}</div>
            <div>Erstellt: {formatDate(video.created)}</div>
          </div>

          {/* Video Attachments */}
          <AdminVideoAttachments videoId={video.id} />
        </div>

        <div className="flex md:flex-col justify-between">
          <div className="flex md:flex-col items-center space-x-3 md:space-x-0 md:space-y-2">
            {video.bunnyStreamVideoID && isVideoReadyToPlay(video) && (
              <Link
                to={`/courses/${video.courseID}/modules/${video.moduleID}/videos/${video.id}`}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                title="Video ansehen"
              >
                <FontAwesomeIcon icon={faPlay} className="w-4 h-4" />
              </Link>
            )}
            {video.providesKnowledge && video.localProcessingState === 'DONE' && (
              <button
                onClick={() => onShowKnowledge(video)}
                className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 cursor-pointer"
                title="Markdown Knowledge anzeigen"
              >
                <FontAwesomeIcon icon={faFileText} className="w-4 h-4" />
              </button>
            )}
            {processingStatus.showErrorAlert && (
              <button
                onClick={handleResetProcessing}
                disabled={isResetting}
                className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 disabled:opacity-50 cursor-pointer"
                title="Zurücksetzen"
              >
                {isResetting ? (
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faRefresh} className="w-4 h-4" />
                )}
              </button>
            )}
            {/* Publish/Unpublish/AutoPublish Button */}
            {processingStatus.canSetPublished && video.published && (
              <button
                onClick={handleUnpublish}
                disabled={isPublishing}
                className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300 disabled:opacity-50 cursor-pointer"
                title="Zurückziehen"
              >
                {isPublishing ? (
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faEyeSlash} className="w-4 h-4" />
                )}
              </button>
            )}
            {processingStatus.canSetPublished && !video.published && (
              <button
                onClick={() => setIsPublishModalOpen(true)}
                disabled={isPublishing}
                className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 disabled:opacity-50 cursor-pointer"
                title="Veröffentlichen"
              >
                {isPublishing ? (
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faRocket} className="w-4 h-4" />
                )}
              </button>
            )}
            {/* Auto-publish settings button during processing */}
            {processingStatus.showProcessingStates && !video.published && !processingStatus.showErrorAlert && (
              <button
                onClick={() => setIsAutoPublishModalOpen(true)}
                disabled={isSavingAutoPublish}
                className={`${video.autoPublish ? 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300' : 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300'} disabled:opacity-50 cursor-pointer`}
                title={video.autoPublish ? 'Auto-Publish aktiv - Einstellungen' : 'Auto-Publish Einstellungen'}
              >
                {isSavingAutoPublish ? (
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faRocket} className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              onClick={() => onEditVideo(video)}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer"
              title="Bearbeiten"
            >
              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
            </button>
            <button
              onClick={() => onMoveVideo(video)}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer"
              title="In anderes Modul verschieben"
            >
              <FontAwesomeIcon icon={faRightLeft} className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDeleteVideo(video.id)}
              disabled={deletingVideo === video.id}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50 cursor-pointer"
              title="Löschen"
            >
              {deletingVideo === video.id ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Publish Modal */}
      <PublishModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onPublish={handlePublish}
        title="Video veröffentlichen"
        itemName={video.title}
        type="video"
      />

      {/* Auto-Publish Settings Modal */}
      <AutoPublishModal
        isOpen={isAutoPublishModalOpen}
        onClose={() => setIsAutoPublishModalOpen(false)}
        video={video}
        onSave={handleAutoPublishUpdate}
      />
    </div>
  );
};

export default AdminVideoCard;
